import React from 'react'
import { Box, Skeleton, Typography, styled } from '@mui/material'
import { Topic } from '@/types'

const Card = styled(Box)({
  position: 'relative',
  height: 320,
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-lift)',
  cursor: 'pointer',
  background: 'var(--ds-night-canvas)',
  transition: 'box-shadow 260ms ease',
  '&:hover': { boxShadow: 'var(--ds-shadow-deep)' },
  '&:hover .spotlight-bg': { transform: 'scale(1.045)' },
  '&:hover .spotlight-cta': { transform: 'translateX(3px)' },
})

const BgImg = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 900ms cubic-bezier(0.16,1,0.3,1)',
})

const Scrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(90deg,
    color-mix(in srgb, var(--ds-ink) 78%, transparent) 0%,
    color-mix(in srgb, var(--ds-ink) 28%, transparent) 48%,
    color-mix(in srgb, var(--ds-ink) 5%, transparent) 75%)`,
})

const Content = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '0 40px',
  maxWidth: '52%',
  color: 'var(--ds-night-ink)',
})

const Eyebrow = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.14em',
  color: 'var(--ds-accent-dot-1)',
  marginBottom: 10,
})

const Title = styled(Typography)({
  fontSize: 30,
  fontWeight: 750,
  letterSpacing: '-0.03em',
  lineHeight: 1.05,
  color: 'var(--ds-night-ink)',
})

const Sub = styled(Box)({
  marginTop: 9,
  fontSize: 13,
  color: 'color-mix(in srgb, var(--ds-night-ink) 72%, transparent)',
})

const Cta = styled(Box)({
  marginTop: 20,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 40,
  padding: '0 20px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-canvas)',
  color: 'var(--ds-ink)',
  fontSize: 13,
  fontWeight: 660,
  width: 'fit-content',
  transition: 'transform 140ms ease',
})

const EmptyWrap = styled(Box)({
  borderRadius: 'var(--ds-radius-xl)',
  border: '1.5px dashed var(--ds-border)',
  height: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  color: 'var(--ds-ink-subtle)',
  fontSize: 14,
})

export interface GallerySpotlightProps {
  topic?: Topic
  coverUrl?: string
  loading?: boolean
  isSelf?: boolean
  onClick?: (topic: Topic) => void
  onEmptyAction?: () => void
}

export function GallerySpotlight({ topic, coverUrl, loading, isSelf, onClick, onEmptyAction }: GallerySpotlightProps) {
  if (loading) {
    return <Skeleton variant='rectangular' sx={{ height: 320, borderRadius: 'var(--ds-radius-xl)' }} />
  }

  if (!topic) {
    return (
      <EmptyWrap
        role={onEmptyAction ? 'button' : undefined}
        tabIndex={onEmptyAction ? 0 : undefined}
        onClick={onEmptyAction}
        onKeyDown={onEmptyAction ? (e) => e.key === 'Enter' && onEmptyAction() : undefined}
        sx={onEmptyAction ? { cursor: 'pointer', '&:hover': { borderColor: 'var(--ds-border-strong)', color: 'var(--ds-ink-muted)' } } : undefined}
      >
        <span style={{ fontSize: 32 }}>◈</span>
        <span>{isSelf ? 'Create your first walkable gallery' : 'No galleries to show yet'}</span>
        {isSelf && <span style={{ fontSize: 12.5, fontWeight: 600 }}>Open Galleries →</span>}
      </EmptyWrap>
    )
  }

  const photoCount = parseInt(topic.photoCount ?? '0', 10) || 0
  const sub = [
    photoCount > 0 ? `${photoCount} ${photoCount === 1 ? 'print' : 'prints'}` : null,
    'Walkable',
  ].filter(Boolean).join(' · ')

  return (
    <Card onClick={() => onClick?.(topic)} role='button' tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick?.(topic)}>
      {coverUrl ? (
        <BgImg className='spotlight-bg' src={coverUrl} alt={topic.topicName} />
      ) : (
        <Box
          className='spotlight-bg'
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 70% 80% at 20% 30%, color-mix(in srgb, var(--ds-accent-dot-1) 35%, transparent), transparent 55%),
              radial-gradient(ellipse 60% 70% at 85% 70%, color-mix(in srgb, var(--ds-accent-dot-4) 28%, transparent), transparent 55%),
              linear-gradient(145deg, var(--ds-night-surface) 0%, var(--ds-night-canvas) 100%)
            `,
          }}
        />
      )}
      <Scrim />
      <Content>
        <Eyebrow>◈ PINNED GALLERY</Eyebrow>
        <Title>{topic.topicName}</Title>
        {sub && <Sub>{sub}</Sub>}
        <Cta className='spotlight-cta'>Walk this room <span>→</span></Cta>
      </Content>
    </Card>
  )
}
